// @givmo/crm — the contact import (CPB-WEB8): "Bring your contacts".
//
// A component, not a page, so any module can rent it: the CRM's own `/crm/import` page frames it,
// and the Fundraisers Emails tab can show it in place later (Email Campaign plan §1.1 step 2). The
// host gives it the organization (`config.orgRef`) and says what happens when the person is done or
// backs out; nothing in it knows which console it is in.
//
// Three screens over one file (mockups 03, 06, 05): choose how (your AI, or upload), match the file's
// columns to Givmo's, and check before importing. Stateless on the server (D-IMP-2): the file stays
// here, and each step sends it again. Every count on the check screen is the server's dry run,
// printed as it came; the import button imports exactly what that dry run described, and carries an
// import id so a double click imports once.
//
// NO MATCHING, EVER. An imported contact is never matched to a Givmo donor; nothing here suggests
// one. Unsubscribes: every column matched to Unsubscribed says, value by value, what counts as
// unsubscribed, so a reversed column cannot mark everyone by mistake — an import's unsubscribe is
// permanent.

import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { firstErrorCode, isGivmoError } from '@givmo/api-client';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  Dialog,
  ErrorMessage,
  Icon,
  Select,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  TextField,
  cn,
  testIdProp,
} from '@givmo/design-system';
import {
  ArrowLeft,
  Bot,
  Check,
  CircleAlert,
  Download,
  FileSpreadsheet,
  Upload,
} from 'lucide-react';
import type { CrmModuleConfig } from './crm-config';
import { displayCount, isTermsRequired, serverMessage } from './crm-decode';
import {
  dryRunCrmImport,
  executeCrmImport,
  fetchCrmImportSheet,
  fileToBase64,
  previewCrmImport,
  type CrmImportFile,
} from './crm-import-transport';
import {
  decodeCrmImportCheck,
  decodeCrmImportPreview,
  decodeCrmImportResult,
  decodeCrmImportSheet,
  type CrmImportCheck,
  type CrmImportColumnIn,
  type CrmImportConsentBasis,
  type CrmImportField,
  type CrmImportPreview,
  type CrmImportResult,
  type CrmImportSheet,
} from './crm-import-wire';

const PREFIX = 'crm-import';
/** The column dropdown's "Skip" (a column matched to no field). */
const SKIP = 'skip';
const BYTE_ORDER_MARK = String.fromCharCode(0xfeff);

/** The largest file the import reads (the backend's own limit, D-IMP caps). */
export const CRM_IMPORT_MAX_FILE_BYTES = 5 * 1024 * 1024;
/** Row numbers listed for new contacts without an email before "and N more". */
const LISTED_ROWS = 10;

/** The words for the fields when the sheet has not loaded (they are the sheet's own headers). */
const FIELD_LABELS: Record<CrmImportField, string> = {
  external_id: 'ID from your old system',
  first_name: 'First name',
  last_name: 'Last name',
  full_name: 'Full name',
  organization_name: 'Organization',
  email: 'Email',
  email_2: 'Second email',
  phone: 'Phone',
  address_line_1: 'Address line 1',
  address_line_2: 'Address line 2',
  city: 'City',
  state: 'State',
  postal_code: 'ZIP code',
  country: 'Country',
  tags: 'Tags',
  notes: 'Notes',
  last_gift_date: 'Last gift date',
  first_gift_date: 'First gift date',
  last_gift_amount: 'Last gift amount',
  lifetime_giving: 'Lifetime giving',
  largest_gift: 'Largest gift',
  deceased: 'Deceased',
  deceased_date: 'Deceased date',
  unsubscribed: 'Unsubscribed',
  consent_source: 'Consent source',
  recurring_flag: 'Recurring gift',
  recurring_amount: 'Recurring amount',
  recurring_frequency: 'Recurring frequency',
};
const MANY: ReadonlySet<CrmImportField> = new Set(['tags', 'notes', 'deceased', 'unsubscribed']);
const TAKES_VALUES: ReadonlySet<CrmImportField> = new Set([
  'deceased',
  'unsubscribed',
  'recurring_flag',
]);
/** The values line's words, per field (mockup 06's "Codes that mean unsubscribed"). */
const VALUES_LINE: Partial<Record<CrmImportField, string>> = {
  unsubscribed: 'Values that mean unsubscribed:',
  deceased: 'Values that mean deceased:',
  recurring_flag: 'Values that mean a recurring gift:',
};

const CONSENT_LABELS: Record<CrmImportConsentBasis, string> = {
  gave: 'They gave to you',
  signed_up: 'They signed up to hear from you',
  took_part: 'They came to an event or volunteered',
  mixed: 'A mix of these',
};

interface ColumnChoice {
  field: CrmImportField | null;
  /** For a yes/no field on a column with few enough values to list: the values that mean yes. */
  values: string[] | null;
}

interface LoadedFile {
  file: CrmImportFile;
  byteSize: number;
}

type Step =
  | { kind: 'choose' }
  | { kind: 'map'; loaded: LoadedFile; preview: CrmImportPreview; choices: ColumnChoice[] }
  | {
      kind: 'check';
      loaded: LoadedFile;
      preview: CrmImportPreview;
      choices: ColumnChoice[];
      check: CrmImportCheck;
      importId: string;
    }
  | { kind: 'done'; result: CrmImportResult };

export interface CrmImportWizardProps {
  config: CrmModuleConfig;
  /** The person is finished (after an import) and wants to see the contacts. */
  onDone: (result: CrmImportResult) => void;
  /** Where "How to connect" your AI goes (the console's connected-AI page), when the host has one. */
  connectAiHref?: string;
  /** Preview and test seam: start on a file already chosen, as if it were just uploaded. */
  initialFile?: { name: string; text: string };
}

function newImportId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : // A version 4 UUID from Math.random, for an environment without crypto.randomUUID.
      'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
        const random = Math.floor(Math.random() * 16);
        return (char === 'x' ? random : (random % 4) + 8).toString(16);
      });
}

function initialChoices(preview: CrmImportPreview): ColumnChoice[] {
  return preview.columns.map((column) => {
    const field = column.suggested_field;
    return {
      field,
      values:
        field !== null && TAKES_VALUES.has(field) && column.values !== null
          ? (column.yes_values[field] ?? [])
          : null,
    };
  });
}

function mappingOf(choices: ColumnChoice[]): CrmImportColumnIn[] {
  const mapping: CrmImportColumnIn[] = [];
  choices.forEach((choice, column) => {
    if (choice.field === null) return;
    mapping.push({
      column,
      field: choice.field,
      ...(choice.values !== null && TAKES_VALUES.has(choice.field)
        ? { values: choice.values }
        : {}),
    });
  });
  return mapping;
}

/** The import id was used for another file (the backend's own code), never the terms gate's 409. */
function isImportIdTaken(error: unknown): boolean {
  return isGivmoError(error) && firstErrorCode(error) === 'CRM_IMPORT_ID_TAKEN';
}

function plural(count: number, one: string, many: string): string {
  return `${displayCount(count)} ${count === 1 ? one : many}`;
}

function rowsList(rows: number[], total: number): string {
  const shown = rows.slice(0, LISTED_ROWS).map((row) => displayCount(row));
  const more = total - shown.length;
  const listed =
    shown.length <= 1
      ? shown.join('')
      : `${shown.slice(0, -1).join(', ')}${more > 0 ? ',' : ''} ${more > 0 ? '' : 'and '}${shown[shown.length - 1]}`;
  return more > 0 ? `${listed} and ${displayCount(more)} more` : listed;
}

export function CrmImportWizard({
  config,
  onDone,
  connectAiHref,
  initialFile,
}: CrmImportWizardProps) {
  const [step, setStep] = useState<Step>({ kind: 'choose' });
  const [sheet, setSheet] = useState<CrmImportSheet | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);

  useEffect(() => {
    let live = true;
    fetchCrmImportSheet(config.orgRef)
      .then((value) => live && setSheet(decodeCrmImportSheet(value)))
      .catch(() => live && setSheet(null));
    return () => {
      live = false;
    };
  }, [config.orgRef]);

  const labels = useMemo(() => {
    const words = { ...FIELD_LABELS };
    for (const row of sheet?.fields ?? []) words[row.field] = row.header;
    return words;
  }, [sheet]);

  async function readFile(file: CrmImportFile, byteSize: number) {
    setPending(true);
    setError(null);
    try {
      const preview = decodeCrmImportPreview(await previewCrmImport(config.orgRef, file));
      if (preview === null) throw new Error('unreadable preview');
      setStep({
        kind: 'map',
        loaded: { file, byteSize },
        preview,
        choices: initialChoices(preview),
      });
    } catch (caught) {
      setError(serverMessage(caught, 'We couldn’t read that file. Please try again.'));
    } finally {
      setPending(false);
    }
  }

  useEffect(() => {
    if (initialFile === undefined || started.current) return;
    started.current = true;
    const bytes = new TextEncoder().encode(initialFile.text);
    void readFile(
      { file_name: initialFile.name, content_base64: btoa(String.fromCharCode(...bytes)) },
      bytes.length,
    );
    // The seam runs once, on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function onFileChosen(file: File | undefined) {
    if (file === undefined) return;
    if (file.size > CRM_IMPORT_MAX_FILE_BYTES) {
      setError('The file is larger than 5 MB. Split it into smaller files and import each one.');
      return;
    }
    await readFile({ file_name: file.name, content_base64: await fileToBase64(file) }, file.size);
  }

  async function runCheck(loaded: LoadedFile, preview: CrmImportPreview, choices: ColumnChoice[]) {
    setPending(true);
    setError(null);
    try {
      const check = decodeCrmImportCheck(
        await dryRunCrmImport(config.orgRef, { ...loaded.file, mapping: mappingOf(choices) }),
      );
      if (check === null) throw new Error('unreadable check');
      setStep({ kind: 'check', loaded, preview, choices, check, importId: newImportId() });
    } catch (caught) {
      setError(serverMessage(caught, 'We couldn’t check the file. Please try again.'));
    } finally {
      setPending(false);
    }
  }

  async function runImport(
    current: Extract<Step, { kind: 'check' }>,
    sourceLabel: string,
    consentBasis: CrmImportConsentBasis,
  ) {
    setPending(true);
    setError(null);
    try {
      const result = decodeCrmImportResult(
        await executeCrmImport(config.orgRef, {
          ...current.loaded.file,
          mapping: mappingOf(current.choices),
          source_label: sourceLabel,
          consent_basis: consentBasis,
          import_id: current.importId,
        }),
      );
      if (result === null) throw new Error('unreadable result');
      setStep({ kind: 'done', result });
    } catch (caught) {
      if (isTermsRequired(caught) && !isImportIdTaken(caught) && config.links.terms !== undefined) {
        setError('Accept the terms before you import contacts.');
      } else {
        setError(serverMessage(caught, 'We couldn’t import the file. Please try again.'));
      }
    } finally {
      setPending(false);
    }
  }

  if (step.kind === 'choose') {
    return (
      <ChooseHow
        pending={pending}
        error={error}
        sheet={sheet}
        connectAiHref={connectAiHref ?? config.links.connectAi}
        onFile={(file) => void onFileChosen(file)}
      />
    );
  }
  if (step.kind === 'map') {
    return (
      <MapColumns
        step={step}
        labels={labels}
        pending={pending}
        error={error}
        onChange={(choices) => setStep({ ...step, choices })}
        onAnotherFile={() => {
          setError(null);
          setStep({ kind: 'choose' });
        }}
        onBack={() => {
          setError(null);
          setStep({ kind: 'choose' });
        }}
        onContinue={() => void runCheck(step.loaded, step.preview, step.choices)}
      />
    );
  }
  if (step.kind === 'check') {
    return (
      <CheckImport
        step={step}
        sheet={sheet}
        pending={pending}
        error={error}
        onBack={() => {
          setError(null);
          setStep({
            kind: 'map',
            loaded: step.loaded,
            preview: step.preview,
            choices: step.choices,
          });
        }}
        onImport={(label, basis) => void runImport(step, label, basis)}
      />
    );
  }
  return <ImportDone result={step.result} onDone={() => onDone(step.result)} />;
}

// ── The frame (the claim wizard's: a cue over a 24px heading, one centred column) ────────────────

function ImportFrame({
  title,
  children,
  footer,
  testId,
}: {
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  testId: string;
}) {
  return (
    <div className="flex w-full justify-center px-24 pt-24 pb-24" {...testIdProp(testId)}>
      <div className="flex w-full max-w-[760px] flex-col gap-20">
        <div className="flex flex-col gap-6">
          <p className="font-sans text-s12 font-w600 uppercase tracking-[0.06em] text-text-secondary">
            Bring your contacts
          </p>
          <h1 className="font-sans text-s24 font-w600 leading-h13 tracking-givmo text-text-strong">
            {title}
          </h1>
        </div>
        {children}
        {footer !== undefined ? (
          <div className="flex flex-wrap items-center justify-end gap-8 border-t border-border-subtle pt-16">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}

// ── Screen 03: choose how ─────────────────────────────────────────────────────────────────────────

function WayCard({
  icon,
  title,
  line,
  badge,
  recommended = false,
  children,
}: {
  icon: typeof Bot;
  title: string;
  line: string;
  badge?: string;
  recommended?: boolean;
  children: ReactNode;
}) {
  return (
    <Card className="flex flex-col gap-16 p-24">
      <div className="flex items-start justify-between gap-12">
        <span
          className={cn(
            'flex size-[44px] items-center justify-center rounded-lg',
            recommended ? 'bg-brand-50 text-brand-600' : 'bg-neutral-100 text-icon',
          )}
        >
          <Icon icon={icon} size={22} aria-hidden />
        </span>
        {badge !== undefined ? (
          <Badge tone="brand" size="md">
            {badge}
          </Badge>
        ) : null}
      </div>
      <div className="flex flex-col gap-6">
        <h2 className="font-sans text-s16 font-w600 tracking-givmo text-text-strong">{title}</h2>
        <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
          {line}
        </p>
      </div>
      <div className="pt-4">{children}</div>
    </Card>
  );
}

function downloadBlankSheet(sheet: CrmImportSheet) {
  // A byte-order mark first, so a spreadsheet opens the sheet as UTF-8.
  const blob = new Blob([`${BYTE_ORDER_MARK}${sheet.csv_header}\r\n`], {
    type: 'text/csv;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = sheet.file_name;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function ChooseHow({
  pending,
  error,
  sheet,
  connectAiHref,
  onFile,
}: {
  pending: boolean;
  error: string | null;
  sheet: CrmImportSheet | null;
  connectAiHref?: string;
  onFile: (file: File | undefined) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [aiOpen, setAiOpen] = useState(false);
  return (
    <ImportFrame title="How would you like to bring them in?" testId={`${PREFIX}-choose`}>
      <div className="grid grid-cols-1 items-stretch gap-16 md:grid-cols-2">
        <WayCard
          recommended
          icon={Bot}
          title="Let my AI prepare it"
          badge="Recommended"
          line="Export your contacts from any CRM or spreadsheet, and your AI maps them for Givmo."
        >
          <div className="flex flex-col items-start gap-10">
            <Button
              appearance="saas"
              tone="primary"
              onClick={() => setAiOpen(true)}
              testId={`${PREFIX}-use-ai`}
            >
              Use my AI
            </Button>
            <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
              Works with Claude, ChatGPT or any AI you’ve connected to Givmo.
              {connectAiHref !== undefined ? (
                <>
                  {' '}
                  <a
                    href={connectAiHref}
                    className="font-w600 text-brand-600 hover:underline"
                    {...testIdProp(`${PREFIX}-how-to-connect`)}
                  >
                    How to connect
                  </a>
                </>
              ) : null}
            </p>
          </div>
        </WayCard>
        <WayCard
          icon={FileSpreadsheet}
          title="Upload a spreadsheet"
          line="Givmo’s import sheet or any spreadsheet. You match the columns yourself."
        >
          <input
            ref={input}
            type="file"
            accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="sr-only"
            onChange={(event) => {
              onFile(event.target.files?.[0]);
              event.target.value = '';
            }}
            {...testIdProp(`${PREFIX}-file-input`)}
          />
          <Button
            appearance="saas"
            tone="secondary"
            leadingIcon={<Icon icon={Upload} size={16} aria-hidden />}
            loading={pending}
            disabled={pending}
            onClick={() => input.current?.click()}
            testId={`${PREFIX}-upload`}
          >
            Upload a file
          </Button>
        </WayCard>
      </div>
      <p className="flex flex-wrap items-center gap-8 font-sans text-s14 font-w400 tracking-givmo text-text-secondary">
        Starting from scratch?
        <Button
          appearance="saas"
          tone="link"
          size="md"
          leadingIcon={<Icon icon={Download} size={16} aria-hidden />}
          disabled={sheet === null}
          onClick={() => sheet !== null && downloadBlankSheet(sheet)}
          testId={`${PREFIX}-blank-sheet`}
        >
          Download the blank import sheet
        </Button>
      </p>
      {error !== null ? <ErrorMessage testId={`${PREFIX}-error`}>{error}</ErrorMessage> : null}
      <AiDialog open={aiOpen} onOpenChange={setAiOpen} connectAiHref={connectAiHref} />
    </ImportFrame>
  );
}

function AiDialog({
  open,
  onOpenChange,
  connectAiHref,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  connectAiHref?: string;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content
        aria-label="Let your AI prepare it"
        className="flex w-[520px] flex-col gap-16"
        testId={`${PREFIX}-ai-dialog`}
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          Let your AI prepare it
        </Dialog.Title>
        <ol className="flex list-decimal flex-col gap-8 pl-20 font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
          <li>Export your contacts from your CRM or spreadsheet, with every column.</li>
          <li>
            Open your AI where it’s connected to Givmo, attach the export, and ask it to get your
            contacts ready to import into Givmo.
          </li>
          <li>
            It matches the columns, keeps everyone who unsubscribed unsubscribed, and shows you what
            will happen. Nothing is imported until you say so.
          </li>
        </ol>
        <div className="flex items-center justify-end gap-8">
          {connectAiHref !== undefined ? (
            <a
              href={connectAiHref}
              className="font-sans text-s14 font-w600 text-brand-600 hover:underline"
            >
              How to connect
            </a>
          ) : null}
          <Dialog.Close
            render={
              <Button appearance="saas" tone="primary" testId={`${PREFIX}-ai-dialog-close`}>
                Got it
              </Button>
            }
          />
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}

// ── Screen 06: match the columns ──────────────────────────────────────────────────────────────────

function MapColumns({
  step,
  labels,
  pending,
  error,
  onChange,
  onAnotherFile,
  onBack,
  onContinue,
}: {
  step: Extract<Step, { kind: 'map' }>;
  labels: Record<CrmImportField, string>;
  pending: boolean;
  error: string | null;
  onChange: (choices: ColumnChoice[]) => void;
  onAnotherFile: () => void;
  onBack: () => void;
  onContinue: () => void;
}) {
  const { preview, choices } = step;
  const options = useMemo(
    () => [
      { value: SKIP, label: 'Skip' },
      ...(Object.keys(labels) as CrmImportField[]).map((field) => ({
        value: field,
        label: labels[field],
      })),
    ],
    [labels],
  );
  const hasIdentity = choices.some(
    (choice) =>
      choice.field !== null &&
      ['email', 'email_2', 'first_name', 'last_name', 'full_name', 'organization_name'].includes(
        choice.field,
      ),
  );

  function choose(column: number, value: string | null) {
    const field = (value === SKIP || value === null ? null : value) as CrmImportField | null;
    const next = choices.map((choice, index) => {
      if (index === column) {
        const values =
          field !== null && TAKES_VALUES.has(field) && preview.columns[column]?.values != null
            ? (preview.columns[column]?.yes_values[field] ?? [])
            : null;
        return { field, values };
      }
      // A field that takes one column moves: the column that had it is skipped.
      return field !== null && !MANY.has(field) && choice.field === field
        ? { field: null, values: null }
        : choice;
    });
    onChange(next);
  }

  function toggleValue(column: number, value: string, checked: boolean) {
    onChange(
      choices.map((choice, index) => {
        if (index !== column || choice.values === null) return choice;
        const values = checked
          ? [...choice.values, value]
          : choice.values.filter((item) => item !== value);
        return { ...choice, values };
      }),
    );
  }

  const matched = preview.columns.filter((column) => choices[column.column]?.field != null);
  const unmatched = preview.columns.filter((column) => choices[column.column]?.field == null);

  function rows(list: typeof preview.columns) {
    return list.map((column) => {
      const choice = choices[column.column] ?? { field: null, values: null };
      const valuesLine =
        choice.field !== null && TAKES_VALUES.has(choice.field) ? VALUES_LINE[choice.field] : null;
      return (
        <Fragment key={column.column}>
          <TR
            className={valuesLine ? 'border-b-0' : undefined}
            testId={`${PREFIX}-column-${column.column}`}
          >
            <TD className="h-[42px]">
              <span className="flex flex-col">
                <span className="font-w600 text-text-strong">{column.header}</span>
                {column.note !== null ? (
                  <span className="text-s12 text-text-tertiary">{column.note}</span>
                ) : null}
              </span>
            </TD>
            <TD className="h-[42px]">
              <span className="text-text-secondary">{column.example ?? '—'}</span>
            </TD>
            <TD align="right" className="h-[42px]">
              <Select
                appearance="saas"
                aria-label={`Givmo field for ${column.header}`}
                options={options}
                value={choice.field ?? SKIP}
                onValueChange={(value) => choose(column.column, value)}
                // A skipped column reads quiet (mockup 06): its trigger's text in the tertiary ink.
                className={cn(
                  'ml-auto w-[208px]',
                  choice.field === null && '[&_[role=combobox]]:text-text-tertiary',
                )}
                testId={`${PREFIX}-field-${column.column}`}
              />
            </TD>
          </TR>
          {valuesLine ? (
            <tr className="border-b border-border-subtle">
              <td colSpan={3} className="px-16 pb-8">
                <div
                  className="flex flex-wrap items-center gap-x-14 gap-y-6 border-l-2 border-border-strong pl-12 font-sans text-s13 tracking-givmo text-text-secondary"
                  {...testIdProp(`${PREFIX}-values-${column.column}`)}
                >
                  {choice.values !== null && column.values !== null ? (
                    <>
                      <span>{valuesLine}</span>
                      {column.values.map((value) => (
                        <label key={value} className="flex items-center gap-6 text-text-strong">
                          <Checkbox
                            appearance="saas"
                            size={18}
                            checked={choice.values?.includes(value) ?? false}
                            onCheckedChange={(checked) =>
                              toggleValue(column.column, value, checked)
                            }
                          />
                          {value}
                        </label>
                      ))}
                    </>
                  ) : (
                    <span>
                      A yes, or words like “{labels[choice.field as CrmImportField]}”, counts.
                    </span>
                  )}
                </div>
              </td>
            </tr>
          ) : null}
        </Fragment>
      );
    });
  }

  return (
    <ImportFrame
      title="Match your columns to Givmo’s"
      testId={`${PREFIX}-map`}
      footer={
        <>
          {error !== null ? (
            <ErrorMessage className="mr-auto" testId={`${PREFIX}-error`}>
              {error}
            </ErrorMessage>
          ) : !hasIdentity ? (
            <span className="mr-auto font-sans text-s13 font-w400 tracking-givmo text-text-tertiary">
              Match a column to Email or to a name.
            </span>
          ) : null}
          <Button
            appearance="saas"
            tone="secondary"
            leadingIcon={<Icon icon={ArrowLeft} size={16} aria-hidden />}
            onClick={onBack}
            disabled={pending}
            testId={`${PREFIX}-back`}
          >
            Back
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            onClick={onContinue}
            loading={pending}
            disabled={pending || !hasIdentity}
            testId={`${PREFIX}-continue`}
          >
            Continue
          </Button>
        </>
      }
    >
      <div className="flex flex-wrap items-center gap-10 font-sans text-s14 tracking-givmo">
        <Icon icon={FileSpreadsheet} size={18} className="text-success-600" aria-hidden />
        <span className="font-w600 text-text-strong" {...testIdProp(`${PREFIX}-file-name`)}>
          {step.loaded.file.file_name}
        </span>
        <span className="text-text-tertiary tabular-nums">
          {plural(preview.row_count, 'row', 'rows')} ·{' '}
          {plural(preview.columns.length, 'column', 'columns')}
        </span>
        <Button
          appearance="saas"
          tone="link"
          size="sm"
          className="ml-auto"
          onClick={onAnotherFile}
          testId={`${PREFIX}-another-file`}
        >
          Choose another file
        </Button>
      </div>
      <Card className="overflow-hidden">
        <Table className="table-fixed">
          <colgroup>
            <col className="w-[40%]" />
            <col className="w-[30%]" />
            <col className="w-[30%]" />
          </colgroup>
          <THead>
            <tr>
              <TH>Column in your file</TH>
              <TH>Example</TH>
              <TH align="right">Givmo field</TH>
            </tr>
          </THead>
          <TBody>
            {rows(matched)}
            {unmatched.length > 0 ? (
              <tr>
                <td
                  colSpan={3}
                  className="border-y border-border-subtle bg-surface-sunken px-16 py-8"
                >
                  <span className="font-sans text-s12 font-w600 tracking-givmo text-text-secondary">
                    Not matched
                  </span>
                </td>
              </tr>
            ) : null}
            {rows(unmatched)}
          </TBody>
        </Table>
      </Card>
    </ImportFrame>
  );
}

// ── Screen 05: check before importing ─────────────────────────────────────────────────────────────

function SummaryRow({
  count,
  testId,
  children,
}: {
  count: number;
  testId: string;
  children: ReactNode;
}) {
  return (
    <div className="flex items-start gap-16 px-20 py-14" {...testIdProp(testId)}>
      <span className="w-[64px] shrink-0 text-right font-sans text-s16 font-w600 tracking-givmo text-text-strong tabular-nums">
        {displayCount(count)}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-4 pt-[1px] font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
        {children}
      </div>
    </div>
  );
}

function Strong({ children }: { children: ReactNode }) {
  return <span className="font-w600 text-text-strong">{children}</span>;
}

function CheckImport({
  step,
  sheet,
  pending,
  error,
  onBack,
  onImport,
}: {
  step: Extract<Step, { kind: 'check' }>;
  sheet: CrmImportSheet | null;
  pending: boolean;
  error: string | null;
  onBack: () => void;
  onImport: (sourceLabel: string, consentBasis: CrmImportConsentBasis) => void;
}) {
  const { check } = step;
  const [sourceLabel, setSourceLabel] = useState(step.preview.suggested_source_label);
  const [editingSource, setEditingSource] = useState(false);
  const [consent, setConsent] = useState<CrmImportConsentBasis | null>(null);
  const [showIssues, setShowIssues] = useState(false);
  const listed = sheet?.consent_bases.map((row) => row.value) ?? [];
  const bases =
    listed.length > 0 ? listed : (Object.keys(CONSENT_LABELS) as CrmImportConsentBasis[]);
  const label = sourceLabel.trim();
  // An import that only records unsubscribes, or only marks contacts a deceased row matches,
  // still has something to do: never block it (an unsubscribe in the file must be recordable).
  const nothing =
    check.new_contacts === 0 &&
    check.existing_unsubscribed === 0 &&
    check.existing_deceased === 0 &&
    check.unsubscribed_addresses === 0;
  // Drafted words (copy round): nothing in the file is read as an unsubscribe unless a column is
  // matched to Unsubscribed, so say so before anyone is imported as someone you can email.
  const unsubscribedMatched = step.choices.some((choice) => choice.field === 'unsubscribed');

  return (
    <ImportFrame
      title="Check before you import"
      testId={`${PREFIX}-check`}
      footer={
        <>
          {error !== null ? (
            <ErrorMessage className="mr-auto" testId={`${PREFIX}-error`}>
              {error}
            </ErrorMessage>
          ) : (
            <span className="mr-auto font-sans text-s13 font-w400 tracking-givmo text-text-tertiary">
              Nothing is saved until you import.
            </span>
          )}
          <Button
            appearance="saas"
            tone="secondary"
            leadingIcon={<Icon icon={ArrowLeft} size={16} aria-hidden />}
            onClick={onBack}
            disabled={pending}
            testId={`${PREFIX}-back`}
          >
            Back
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            onClick={() => consent !== null && onImport(label, consent)}
            loading={pending}
            disabled={pending || consent === null || label.length === 0 || nothing}
            testId={`${PREFIX}-execute`}
          >
            {check.new_contacts > 0
              ? `Import ${plural(check.new_contacts, 'contact', 'contacts')}`
              : 'Import'}
          </Button>
        </>
      }
    >
      <Card className="divide-y divide-border-subtle" {...testIdProp(`${PREFIX}-summary`)}>
        <SummaryRow count={check.new_contacts} testId={`${PREFIX}-new`}>
          <Strong>New contacts</Strong>
          {check.new_unsubscribed > 0 ? (
            <span>
              {displayCount(check.new_unsubscribed)} of them{' '}
              {check.new_unsubscribed === 1 ? 'is' : 'are'} unsubscribed and stay that way.
            </span>
          ) : null}
          {check.new_without_email > 0 ? (
            <span>
              {displayCount(check.new_without_email)}{' '}
              {check.new_without_email === 1 ? 'has' : 'have'} no email address, so they won’t get
              emails: {check.new_without_email === 1 ? 'row' : 'rows'}{' '}
              {rowsList(check.new_without_email_rows, check.new_without_email)}.
            </span>
          ) : null}
        </SummaryRow>
        {check.combined > 0 ? (
          <SummaryRow count={check.combined} testId={`${PREFIX}-combined`}>
            <Strong>Duplicates combined</Strong>
            <span>Rows with the same email or ID become one contact.</span>
          </SummaryRow>
        ) : null}
        {check.existing > 0 ? (
          <SummaryRow count={check.existing} testId={`${PREFIX}-existing`}>
            <Strong>Already in your CRM</Strong>
            <span>
              Left as they are.
              {check.existing_unsubscribed > 0
                ? ` ${displayCount(check.existing_unsubscribed)} of them will be marked unsubscribed.`
                : ''}
            </span>
          </SummaryRow>
        ) : null}
        {check.left_out > 0 ? (
          <SummaryRow count={check.left_out} testId={`${PREFIX}-left-out`}>
            <Strong>Left out</Strong>
            <span>
              Marked deceased.
              {check.existing_deceased > 0
                ? ` ${displayCount(check.existing_deceased)} of them ${
                    check.existing_deceased === 1 ? 'is' : 'are'
                  } already in your CRM and won’t get emails.`
                : ''}
            </span>
          </SummaryRow>
        ) : null}
        {check.invalid > 0 ? (
          <SummaryRow count={check.invalid} testId={`${PREFIX}-invalid`}>
            <Strong>Can’t be imported</Strong>
            <span>They have no name and no email address.</span>
          </SummaryRow>
        ) : null}
      </Card>

      {!unsubscribedMatched ? (
        <div
          className="flex items-start gap-10 rounded-lg border border-warning-100 bg-warning-50 p-16"
          {...testIdProp(`${PREFIX}-no-unsubscribed`)}
        >
          <Icon
            icon={CircleAlert}
            size={18}
            className="mt-[1px] shrink-0 text-warning-700"
            aria-hidden
          />
          <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-warning-700">
            <span className="font-w600">No column is matched to Unsubscribed.</span> If your file
            records who doesn’t want email, go back and match that column.
          </p>
        </div>
      ) : null}

      {check.issues.length > 0 ? (
        <div className="flex flex-col gap-8">
          <Button
            appearance="saas"
            tone="link"
            size="sm"
            className="self-start"
            onClick={() => setShowIssues((open) => !open)}
            testId={`${PREFIX}-issues-toggle`}
          >
            {showIssues
              ? 'Hide the notes on rows'
              : `See the notes on ${plural(check.issues.length, 'row', 'rows')}`}
          </Button>
          {showIssues ? (
            <Card className="max-h-[280px] overflow-y-auto" {...testIdProp(`${PREFIX}-issues`)}>
              <ul className="divide-y divide-border-subtle">
                {check.issues.map((issue) => (
                  <li
                    key={`${issue.row_number}-${issue.field ?? ''}-${issue.message}`}
                    className="flex gap-12 px-16 py-8 font-sans text-s13 tracking-givmo text-text-secondary"
                  >
                    <span className="w-[72px] shrink-0 tabular-nums text-text-tertiary">
                      Row {displayCount(issue.row_number)}
                    </span>
                    <span>{issue.message}</span>
                  </li>
                ))}
                {check.issues_truncated ? (
                  <li className="px-16 py-8 font-sans text-s13 tracking-givmo text-text-tertiary">
                    The first 200 notes are shown.
                  </li>
                ) : null}
              </ul>
            </Card>
          ) : null}
        </div>
      ) : null}

      <div className="flex flex-col gap-12">
        <div
          className="flex flex-wrap items-center gap-8 font-sans text-s14 font-w400 tracking-givmo text-text-secondary"
          {...testIdProp(`${PREFIX}-source`)}
        >
          <span className="text-text-tertiary">Source recorded on each contact:</span>
          {editingSource ? (
            <TextField
              aria-label="Source recorded on each contact"
              value={sourceLabel}
              maxLength={200}
              onChange={(event) => setSourceLabel(event.target.value)}
              onBlur={() => setEditingSource(false)}
              className="w-[320px]"
              testId={`${PREFIX}-source-input`}
            />
          ) : (
            <>
              <span className="font-w600 text-text-strong">{label || '—'}</span>
              <Button
                appearance="saas"
                tone="link"
                size="sm"
                onClick={() => setEditingSource(true)}
                testId={`${PREFIX}-source-edit`}
              >
                Change
              </Button>
            </>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-8 font-sans text-s14 font-w400 tracking-givmo text-text-secondary">
          <span className="text-text-tertiary">How they agreed to hear from you:</span>
          <Select
            appearance="saas"
            aria-label="How they agreed to hear from you"
            placeholder="Choose one"
            options={bases.map((value) => ({ value, label: CONSENT_LABELS[value] }))}
            value={consent}
            onValueChange={(value) => setConsent((value as CrmImportConsentBasis | null) ?? null)}
            className="w-[300px]"
            testId={`${PREFIX}-consent`}
          />
        </div>
        <p className="font-sans text-s13 font-w400 tracking-givmo text-text-tertiary">
          Only import people who agreed to hear from you. Bought or rented lists aren’t allowed.
        </p>
      </div>
    </ImportFrame>
  );
}

// ── Done ──────────────────────────────────────────────────────────────────────────────────────────

function ImportDone({ result, onDone }: { result: CrmImportResult; onDone: () => void }) {
  return (
    <ImportFrame
      title={
        result.new_contacts > 0
          ? `${plural(result.new_contacts, 'contact', 'contacts')} imported`
          : 'Your contacts are up to date'
      }
      testId={`${PREFIX}-done`}
      footer={
        <Button appearance="saas" tone="primary" onClick={onDone} testId={`${PREFIX}-see-contacts`}>
          See your contacts
        </Button>
      }
    >
      <Card className="flex items-start gap-12 p-20">
        <span className="flex size-[32px] shrink-0 items-center justify-center rounded-full bg-success-50 text-success-600">
          <Icon icon={Check} size={18} aria-hidden />
        </span>
        <div className="flex flex-col gap-4 font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
          {result.new_unsubscribed + result.existing_unsubscribed > 0 ? (
            <span>
              {plural(
                result.new_unsubscribed + result.existing_unsubscribed,
                'contact is',
                'contacts are',
              )}{' '}
              unsubscribed and won’t get your emails.
            </span>
          ) : null}
          {result.existing > 0 ? (
            <span>
              {plural(result.existing, 'contact was', 'contacts were')} already in your CRM.
            </span>
          ) : null}
          <span>Source recorded on each contact: {result.source_label ?? '—'}.</span>
        </div>
      </Card>
    </ImportFrame>
  );
}

/** "Bring your contacts": the contacts page's way into the import (mockup 02). */
export function BringContactsButton({
  config,
  size = 'md',
}: {
  config: CrmModuleConfig;
  size?: 'sm' | 'md';
}) {
  const navigate = useNavigate();
  const target = config.links.import;
  if (target === undefined) return null;
  return (
    <Button
      appearance="saas"
      tone="primary"
      size={size}
      leadingIcon={<Icon icon={Upload} size={16} aria-hidden />}
      onClick={() => void navigate({ to: target })}
      testId={`${PREFIX}-bring`}
    >
      Bring your contacts
    </Button>
  );
}

export function isCrmImportUnavailable(error: unknown): boolean {
  return isGivmoError(error) && error.status === 404;
}
